import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CLINICIAN_READING_LABEL,
  MEASUREMENT_BOUNDS,
  type Measurement,
  type MeasurementComponents,
  type MeasurementReviewStatus,
} from "@care-by-ella/contracts";
import {
  correctMeasurementSession,
  fetchPatientMeasurements,
  ForbiddenError,
  recordVerifiedMeasurement,
  UnauthorizedError,
} from "@/lib/clinician-api";
import {
  askPatientMessage,
  canAskPatient,
  canDoctorCorrect,
  canReviewOccasion,
  EMPTY_VERIFIED_DRAFT,
  parseVerifiedDraft,
  type VerifiedDraft,
} from "@/lib/measurement-actions";
import { useAuth } from "@/auth/AuthContext";
import { MeasurementCharts } from "./MeasurementCharts";
import { MeasurementReviewControl } from "./MeasurementReview";
import { SendDoctorNotice } from "./SendDoctorNotice";
import { useReadOnly } from "./SupportView";

// TES-863 (Ö-211) — the doctor's correction of a measured value, BESIDE the
// original.
//
// Emelie 2026-09-22, verbatim:
// "Patientens ursprungliga formulärsvar är oföränderliga original. Kirill korrigerar den strukturerade, aktuella medicinska profilen genom en rättad version bredvid originalet."
//
// The server has taken corrections since #866; nothing in the admin called it.
// Three rules shape this component:
//
//   · The OCCASION is the unit, not the value. Supersession is session-level,
//     so a correction carries every component of the occasion — the form is
//     pre-filled with all of them, and the ones he leaves alone are carried
//     over unchanged instead of being dropped.
//   · The original stays visible, muted and marked replaced. "Beside" is only
//     true on screen if the original is on screen.
//   · The reason is compulsory, and a correction that changes nothing is
//     refused before it is sent — it would record a clinical correction of a
//     value that was right.
//
// Doctor only, like the route. An admin opening the page gets 403 from the
// list and the section does not render at all.
//
// TES-999 (Ö-239) — the weight is the patient's to correct, not his. Emelie
// 2026-09-29:
// "Kirill kan se värde, källa och rättelsehistorik samt be patienten rätta ett värde, men inte göra rättelsen åt henne."
// So the weight is read-only in his form (the server refuses a change too),
// a patient's own correction is labelled with its correction time, and a
// weight occasion offers "Be patienten rätta" — a chat message to her,
// pre-filled and editable, since the correction itself is hers to make.
//
// TES-1034 (Emelie's point 7) — the same now holds for her blood pressure and
// resting pulse: no "Rätta" on her occasion (the server refuses it too), but
// "Be patienten rätta", and a reading of his own for a value the doctor
// actually has — a new occasion beside hers. The rules are in
// `lib/measurement-actions.ts`.
//
// TES-1056 (TES-1054 point F) — her blood pressure / pulse occasions carry the
// doctor's assessment (Emelie's three labels, MEASUREMENT_REVIEW_STATUS_LABEL),
// which he sets here and an admin only reads. His own reading is labelled
// CLINICIAN_READING_LABEL (Simon 2026-10-01), never "verifierad".

type Scalar = Exclude<keyof MeasurementComponents, "bloodPressure">;

interface Occasion {
  id: string;
  measuredAt: string;
  source: Measurement["source"];
  current: boolean;
  // TES-999 — set when this occasion corrected an earlier one; `recordedAt`
  // is then the correction time.
  supersedesSessionId: string | null;
  recordedAt: string | null;
  // TES-1056 — the doctor's assessment of the occasion.
  reviewStatus: MeasurementReviewStatus;
  components: MeasurementComponents;
}

const SCALARS: { key: Scalar; label: string }[] = [
  { key: "weight", label: "Vikt" },
  { key: "height", label: "Längd" },
  { key: "waist", label: "Midja" },
  { key: "hip", label: "Höft" },
  { key: "pulse", label: "Vilopuls" },
];

const UNIT: Record<Scalar, string> = {
  weight: "kg",
  height: "cm",
  waist: "cm",
  hip: "cm",
  pulse: "slag/min",
};

const SOURCE_LABEL: Record<Measurement["source"], string> = {
  patient: "Patienten",
  form: "Formulär",
  clinician: "Rättad av läkare",
  import: "Import",
};

const SHOWN_BY_DEFAULT = 8;

function groupIntoOccasions(rows: Measurement[]): Occasion[] {
  const byId = new Map<string, Occasion>();
  for (const m of rows) {
    const o = byId.get(m.sessionId) ?? {
      id: m.sessionId,
      measuredAt: m.measuredAt,
      source: m.source,
      current: m.current,
      supersedesSessionId: m.supersedesSessionId ?? null,
      recordedAt: m.recordedAt ?? null,
      reviewStatus: m.reviewStatus ?? "patient_reported",
      components: {},
    };
    if (m.type === "blood_pressure") {
      if (m.systolic != null && m.diastolic != null) {
        o.components.bloodPressure = {
          systolic: m.systolic,
          diastolic: m.diastolic,
        };
      }
    } else if (m.value != null) {
      o.components[m.type as Scalar] = m.value;
    }
    byId.set(m.sessionId, o);
  }
  // Newest first. A correction keeps the occasion's own date, so it ties with
  // the original it replaces — on a tie the value that counts goes first.
  return [...byId.values()].sort(
    (a, b) =>
      b.measuredAt.localeCompare(a.measuredAt) ||
      Number(b.current) - Number(a.current),
  );
}

function summarize(c: MeasurementComponents): string {
  const parts: string[] = [];
  if (c.bloodPressure) {
    parts.push(`${c.bloodPressure.systolic}/${c.bloodPressure.diastolic} mmHg`);
  }
  for (const { key, label } of SCALARS) {
    const v = c[key];
    if (v !== undefined) parts.push(`${label} ${v} ${UNIT[key]}`);
  }
  return parts.join(" · ") || "—";
}

// Who made the occasion, and for a correction when. A patient's own
// correction reads differently from her first entry: the value in the
// history above it is the one she replaced.
function sourceLabel(o: Occasion): string {
  if (o.source === "patient" && o.supersedesSessionId) {
    return o.recordedAt
      ? `Rättad av patienten ${fmtDate(o.recordedAt)}`
      : "Rättad av patienten";
  }
  if (o.source === "clinician" && !o.supersedesSessionId) {
    return o.recordedAt
      ? `${CLINICIAN_READING_LABEL} ${fmtDate(o.recordedAt)}`
      : CLINICIAN_READING_LABEL;
  }
  if (o.source === "clinician" && o.recordedAt) {
    return `${SOURCE_LABEL.clinician} ${fmtDate(o.recordedAt)}`;
  }
  return SOURCE_LABEL[o.source];
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString("sv-SE", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

export function PatientMeasurements({
  patientId,
  onUnauthorized,
}: {
  patientId: string;
  onUnauthorized?: () => void;
}) {
  // TES-1040 — the admin's read-only view: no correction, no request to the
  // patient to correct, no reading of his own, and the assessment as a tag
  // only.
  const readOnly = useReadOnly();
  // TES-1147 — the request to correct is a notice the doctor signs.
  const isDoctor = useAuth().claims?.role === "doctor";
  const [rows, setRows] = useState<Measurement[] | null>(null);
  const [hidden, setHidden] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [asking, setAsking] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetchPatientMeasurements(patientId);
      setRows(res.measurements);
      setError(null);
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorized?.();
        return;
      }
      if (e instanceof ForbiddenError) {
        setHidden(true);
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [patientId, onUnauthorized]);

  useEffect(() => {
    void load();
  }, [load]);

  const occasions = useMemo(
    () => (rows ? groupIntoOccasions(rows) : []),
    [rows],
  );

  if (hidden) return null;

  const shown = showAll ? occasions : occasions.slice(0, SHOWN_BY_DEFAULT);

  return (
    <section className="rounded-md border border-surfaceMuted bg-surface p-md">
      <h2 className="mb-xs font-display text-lg">Mätvärden</h2>
      <p className="mb-sm text-xs text-textSecondary">
        Varje mättillfälle, äldre och ersatta inräknade. En rättelse läggs
        bredvid originalet — originalet ändras aldrig. Patientens egen vikt,
        blodtryck och vilopuls rättas av henne; har du en egen mätning
        registreras den som ett nytt mättillfälle, märkt Mätning registrerad av
        läkare.
      </p>
      {/* TES-1481 (Ö-805) — her charts first, the occasions behind them
          below. */}
      {rows ? <MeasurementCharts measurements={rows} /> : null}
      {readOnly ? null : recording ? (
        <VerifiedMeasurementForm
          patientId={patientId}
          onCancel={() => setRecording(false)}
          onSaved={() => {
            setRecording(false);
            void load();
          }}
          onUnauthorized={onUnauthorized}
        />
      ) : (
        <button
          type="button"
          onClick={() => setRecording(true)}
          className="mb-sm rounded-md border border-surfaceMuted px-sm py-xs text-xs"
        >
          Registrera egen mätning
        </button>
      )}
      {error ? <p className="text-sm text-danger">{error}</p> : null}
      {rows === null && !error ? (
        <p className="text-sm text-textSecondary">Hämtar…</p>
      ) : null}
      {rows !== null && occasions.length === 0 ? (
        <p className="text-sm text-textSecondary">
          Inga mätvärden registrerade.
        </p>
      ) : null}
      <ul className="flex flex-col divide-y divide-surfaceMuted">
        {shown.map((o) => (
          <li key={o.id} className="py-sm">
            <div className="flex flex-wrap items-baseline gap-sm text-sm">
              <span className="text-xs text-textSecondary">
                {fmtDate(o.measuredAt)}
              </span>
              <span
                className={o.current ? "" : "text-textSecondary line-through"}
              >
                {summarize(o.components)}
              </span>
            </div>
            <div className="mt-xs flex flex-wrap items-center gap-sm text-xs text-textSecondary">
              <span>{sourceLabel(o)}</span>
              {canReviewOccasion(o) ? (
                <MeasurementReviewControl
                  patientId={patientId}
                  sessionId={o.id}
                  status={o.reviewStatus}
                  onUnauthorized={onUnauthorized}
                  onChanged={(status) =>
                    setRows(
                      (prev) =>
                        prev?.map((m) =>
                          m.sessionId === o.id
                            ? { ...m, reviewStatus: status }
                            : m,
                        ) ?? prev,
                    )
                  }
                />
              ) : null}
              {o.current ? null : (
                <span className="rounded-pill border border-surfaceMuted px-sm">
                  Ersatt
                </span>
              )}
              {!readOnly && isDoctor && canAskPatient(o) && asking !== o.id ? (
                <button
                  type="button"
                  onClick={() => setAsking(o.id)}
                  className="ml-auto underline"
                >
                  Be patienten rätta
                </button>
              ) : null}
              {!readOnly && canDoctorCorrect(o) && editing !== o.id ? (
                <button
                  type="button"
                  onClick={() => setEditing(o.id)}
                  className={
                    canAskPatient(o) ? "underline" : "ml-auto underline"
                  }
                >
                  Rätta
                </button>
              ) : null}
            </div>
            {asking === o.id ? (
              <AskPatientToCorrect
                patientId={patientId}
                occasion={o}
                onDone={() => setAsking(null)}
                onUnauthorized={onUnauthorized}
              />
            ) : null}
            {editing === o.id ? (
              <CorrectionForm
                patientId={patientId}
                occasion={o}
                onCancel={() => setEditing(null)}
                onSaved={() => {
                  setEditing(null);
                  void load();
                }}
                onUnauthorized={onUnauthorized}
              />
            ) : null}
          </li>
        ))}
      </ul>
      {occasions.length > SHOWN_BY_DEFAULT ? (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="mt-sm text-xs text-textSecondary underline"
        >
          {showAll ? "Visa färre" : `Visa alla ${occasions.length}`}
        </button>
      ) : null}
    </section>
  );
}

type Draft = Partial<Record<Scalar | "systolic" | "diastolic", string>>;

function toDraft(c: MeasurementComponents): Draft {
  const d: Draft = {};
  for (const { key } of SCALARS) {
    if (c[key] !== undefined) d[key] = String(c[key]);
  }
  if (c.bloodPressure) {
    d.systolic = String(c.bloodPressure.systolic);
    d.diastolic = String(c.bloodPressure.diastolic);
  }
  return d;
}

// The draft back into components, or the first problem with it. Only the
// fields the occasion already had are editable: adding a value the patient
// never measured is not a correction of that occasion.
function fromDraft(
  d: Draft,
  original: MeasurementComponents,
): { components: MeasurementComponents } | { problem: string } {
  const out: MeasurementComponents = {};
  for (const { key, label } of SCALARS) {
    if (original[key] === undefined) continue;
    const n = Number(d[key]);
    const b = MEASUREMENT_BOUNDS[key];
    if (d[key] === undefined || d[key]!.trim() === "" || !Number.isFinite(n)) {
      return { problem: `Ange ${label.toLowerCase()}.` };
    }
    if (n < b.min || n > b.max) {
      return {
        problem: `${label} ska vara mellan ${b.min} och ${b.max} ${UNIT[key]}.`,
      };
    }
    out[key] = n;
  }
  if (original.bloodPressure) {
    const s = Number(d.systolic);
    const di = Number(d.diastolic);
    if (!Number.isInteger(s) || s < 50 || s > 300) {
      return { problem: "Övertrycket ska vara ett heltal mellan 50 och 300." };
    }
    if (!Number.isInteger(di) || di < 30 || di > 200) {
      return { problem: "Undertrycket ska vara ett heltal mellan 30 och 200." };
    }
    out.bloodPressure = { systolic: s, diastolic: di };
  }
  return { components: out };
}

function same(a: MeasurementComponents, b: MeasurementComponents): boolean {
  return summarize(a) === summarize(b);
}

function CorrectionForm({
  patientId,
  occasion,
  onCancel,
  onSaved,
  onUnauthorized,
}: {
  patientId: string;
  occasion: Occasion;
  onCancel: () => void;
  onSaved: () => void;
  onUnauthorized?: () => void;
}) {
  const [draft, setDraft] = useState<Draft>(() => toDraft(occasion.components));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parsed = fromDraft(draft, occasion.components);
  const problem = "problem" in parsed ? parsed.problem : null;
  const unchanged =
    "components" in parsed && same(parsed.components, occasion.components);

  async function save() {
    if (!("components" in parsed)) return;
    setBusy(true);
    setError(null);
    try {
      await correctMeasurementSession(patientId, {
        // The occasion keeps its own date: a correction says what was
        // actually measured THEN, not a new reading taken now.
        measuredAt: occasion.measuredAt,
        supersedesSessionId: occasion.id,
        reason: reason.trim(),
        components: parsed.components,
      });
      onSaved();
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorized?.();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const field = (key: keyof Draft, label: string, unit: string) => (
    <label key={key} className="block text-xs text-textSecondary">
      {label} ({unit})
      <input
        type="number"
        inputMode="decimal"
        value={draft[key] ?? ""}
        // TES-999 — the weight rides along unchanged; the server refuses
        // anything else.
        readOnly={key === "weight"}
        onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
        className="mt-xs w-full rounded-md border border-surfaceMuted p-xs text-xs read-only:opacity-60"
      />
    </label>
  );

  return (
    <div className="mt-sm rounded-md border border-surfaceMuted p-sm">
      <div className="grid grid-cols-2 gap-sm sm:grid-cols-3">
        {occasion.components.bloodPressure ? (
          <>
            {field("systolic", "Övertryck", "mmHg")}
            {field("diastolic", "Undertryck", "mmHg")}
          </>
        ) : null}
        {SCALARS.filter(
          ({ key }) => occasion.components[key] !== undefined,
        ).map(({ key, label }) => field(key, label, UNIT[key]))}
      </div>
      <label className="mt-sm block text-xs text-textSecondary">
        Orsak (obligatorisk, syns inte för patienten)
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          className="mt-xs w-full rounded-md border border-surfaceMuted p-xs text-xs"
        />
      </label>
      <p className="mt-xs text-xs text-textSecondary">
        Originalet från {fmtDate(occasion.measuredAt)} sparas och visas som
        ersatt.
      </p>
      {/* TES-999 (Ö-239) — said where he would otherwise try. */}
      {occasion.components.weight !== undefined ? (
        <p className="mt-xs text-xs text-textSecondary">
          Vikten är patientens egen registrering och kan bara rättas av henne.
          Den följer med oförändrad.
        </p>
      ) : null}
      <div className="mt-sm flex gap-xs">
        <button
          type="button"
          disabled={busy || !reason.trim() || problem !== null || unchanged}
          onClick={() => void save()}
          className="rounded-md border border-surfaceMuted px-sm py-xs text-xs disabled:opacity-50"
        >
          {busy ? "Sparar…" : "Spara rättelse"}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onCancel}
          className="rounded-md px-sm py-xs text-xs text-textSecondary"
        >
          Avbryt
        </button>
      </div>
      {problem ? <p className="mt-xs text-xs text-danger">{problem}</p> : null}
      {unchanged && !problem ? (
        <p className="mt-xs text-xs text-textSecondary">
          Samma värden som originalet — ingenting att rätta.
        </p>
      ) : null}
      {error ? <p className="mt-xs text-xs text-danger">{error}</p> : null}
    </div>
  );
}

// TES-999 (Ö-239) — "be patienten rätta ett värde", pre-filled with the
// value and date so she can find it, and editable because it goes out in his
// name.
//
// TES-1147 (Emelie 2026-10-07) — no longer a doctor-chat message: asking her
// to do something is a medical notice under Din vård ("Skicka besked"; with
// a question when he needs her answer, TES-1332). A doctor chat message
// would open a chat she has no thread for, and the server refuses that.
function AskPatientToCorrect({
  patientId,
  occasion,
  onDone,
  onUnauthorized,
}: {
  patientId: string;
  occasion: Occasion;
  onDone: () => void;
  onUnauthorized?: () => void;
}) {
  const date = new Date(occasion.measuredAt).toLocaleDateString("sv-SE", {
    day: "numeric",
    month: "long",
  });
  return (
    <div className="mt-sm">
      <SendDoctorNotice
        patientId={patientId}
        onUnauthorized={() => onUnauthorized?.()}
        initialBody={askPatientMessage(occasion.components, date)}
        startOpen
        onDone={onDone}
      />
    </div>
  );
}

// TES-1034 — the doctor's own reading: a blood pressure and/or resting pulse
// he actually has (measured at the clinic, from another provider's record),
// with its basis. A NEW occasion; hers stays as it is. TES-1056: shown as
// "Mätning registrerad av läkare", never "verifierad".
function VerifiedMeasurementForm({
  patientId,
  onCancel,
  onSaved,
  onUnauthorized,
}: {
  patientId: string;
  onCancel: () => void;
  onSaved: () => void;
  onUnauthorized?: () => void;
}) {
  const [draft, setDraft] = useState<VerifiedDraft>(EMPTY_VERIFIED_DRAFT);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  const parsed = parseVerifiedDraft(draft);
  const problem = "problem" in parsed ? parsed.problem : null;

  async function save() {
    setTried(true);
    if (!("body" in parsed)) return;
    setBusy(true);
    setError(null);
    try {
      await recordVerifiedMeasurement(patientId, parsed.body);
      onSaved();
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorized?.();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const input = (key: keyof VerifiedDraft, label: string, type = "number") => (
    <label className="block text-xs text-textSecondary">
      {label}
      <input
        type={type}
        inputMode={type === "number" ? "numeric" : undefined}
        value={draft[key]}
        onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
        className="mt-xs w-full rounded-md border border-surfaceMuted p-xs text-xs"
      />
    </label>
  );

  return (
    <div className="mb-sm rounded-md border border-surfaceMuted p-sm">
      <p className="mb-xs text-xs font-semibold">Registrera egen mätning</p>
      <div className="grid grid-cols-2 gap-sm sm:grid-cols-4">
        {input("measuredAt", "Mättidpunkt", "datetime-local")}
        {input("systolic", "Övertryck (mmHg)")}
        {input("diastolic", "Undertryck (mmHg)")}
        {input("pulse", "Vilopuls (slag/min)")}
      </div>
      <label className="mt-sm block text-xs text-textSecondary">
        Underlag (obligatoriskt, t.ex. var och av vem mätningen gjordes)
        <textarea
          value={draft.basis}
          onChange={(e) => setDraft({ ...draft, basis: e.target.value })}
          rows={2}
          className="mt-xs w-full rounded-md border border-surfaceMuted p-xs text-xs"
        />
      </label>
      <p className="mt-xs text-xs text-textSecondary">
        Sparas som en ny mätning bredvid patientens egna värden. Hennes
        registreringar ändras inte.
      </p>
      <div className="mt-sm flex gap-xs">
        <button
          type="button"
          disabled={busy}
          onClick={() => void save()}
          className="rounded-md border border-surfaceMuted px-sm py-xs text-xs disabled:opacity-50"
        >
          {busy ? "Sparar…" : "Spara mätning"}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onCancel}
          className="rounded-md px-sm py-xs text-xs text-textSecondary"
        >
          Avbryt
        </button>
      </div>
      {tried && problem ? (
        <p className="mt-xs text-xs text-danger">{problem}</p>
      ) : null}
      {error ? <p className="mt-xs text-xs text-danger">{error}</p> : null}
    </div>
  );
}
